import { gameState } from "appworks/model/game-state";
import { deepClone } from "appworks/utils/collection-utils";
import { gamevyForceResults } from "gamevy/debug/menu/gamevy-force-results";
import { GamevyForceResultTab } from "gamevy/debug/menu/gamevy-force-tab";
import { gamevyModel } from "gamevy/gamevy-model";
import { AdditionalForceData } from "gamevy/integration/builders/requests/force-request-payload";
import { DebugMenu } from "slotworks/debug/menu/debug-menu";
import { InfoTab } from "slotworks/debug/menu/tabs/info-tab";
import { SpinRecord } from "slotworks/model/gameplay/records/spin-record";
import { slotDefinition } from "slotworks/model/slot-definition";

const commonUIConfig = require("@gluckgames/commonui/package.json");

export interface GamevyForceDataEntry {
    forceMenuLabel: string;
    data: AdditionalForceData;
}

class GamevyForceLoader {
    public reelsets: { [key: string]: string[][] } = {};

    private forces: GamevyForceDataEntry[];

    public setHelperData(reelsets: { [key: string]: string[][] }) {
        this.reelsets = reelsets;
        (window as any).gamevyForceHelper = this.gamevyForceHelper.bind(this);
        (window as any).getCurrentForce = this.getCurrentForce.bind(this);
    }

    public getReelset(id: string): string[][] {
        return this.reelsets[id];
    }

    public setForceData(forces: GamevyForceDataEntry[]) {
        this.forces = forces;
    }

    public getSymbolIdReelsets(): Map<string, string[][]> {
        const reelsets: Map<string, string[][]> = new Map();
        for (const reelsetId in this.reelsets) {
            const reelset = this.reelsets[reelsetId];
            const indexReelset = reelset.map((reel) => {
                return reel.map((symbolStr) => {
                    const symbolIndex = gamevyModel.symbolMap.get(symbolStr);
                    const symbolId = slotDefinition.symbolDefinitions[symbolIndex].id;
                    return symbolId;
                });
            });

            reelsets.set(reelsetId, indexReelset);
        }

        return reelsets;
    }

    public populateForceMenu() {
        const map: Map<string, AdditionalForceData> = new Map<string, AdditionalForceData>();
        this.forces.forEach((entry) => {
            map.set(entry.forceMenuLabel, entry.data);
        });
        gamevyForceResults.setForceData(map);

        DebugMenu.add(new GamevyForceResultTab("Server", gamevyForceResults));

        InfoTab.addInfo("Common UI", commonUIConfig.version);
    }

    protected getReelsWithSymbolNames(reels: string[][]) {
        const newReels = deepClone(reels);

        for (const reel of newReels) {
            for (let y = 0; y < reel.length; y++) {
                const symbolId = reel[y];
                const symbolIndex = gamevyModel.symbolMap.get(symbolId);
                if (symbolIndex !== undefined) {
                    reel[y] = slotDefinition.symbolDefinitions[symbolIndex].id;
                }
            }
        }

        return newReels;
    }

    protected gamevyForceHelper(reelset: string | string[][], pattern: string[][], useSymbolMap: boolean = true): number[] {
        let reels = typeof reelset === "string" ? this.reelsets[reelset] : reelset;

        if (useSymbolMap && gamevyModel.symbolMap.size) {
            reels = this.getReelsWithSymbolNames(reels);
            pattern = this.getReelsWithSymbolNames(pattern);
        }

        return reels.map((reel, index) => {
            for (let i = 0; i < reel.length; i++) {
                let match: boolean = true;
                for (let p = 0; p < pattern[index].length; p++) {
                    if (pattern[index][p] === "*") {
                        continue;
                    }
                    if (reel[i + p] !== pattern[index][p]) {
                        match = false;
                        break;
                    }
                }

                if (match) {
                    return i;
                }
            }

            return -1;
        });
    }

    protected getCurrentForce(): { reelsetId: string, force: number[] } {
        const record = gameState.getCurrentGame().getCurrentRecord() as SpinRecord;
        if (!record) {
            return;
        }

        const reelset = record.reelsetId ?? record.grid;
        const force = this.gamevyForceHelper(reelset, record.grid, true);

        return {
            reelsetId: record.reelsetId,
            force
        };
    }
}

export const gamevyForceLoader = new GamevyForceLoader();
